import { layerPropertiesAtom } from '@/atoms/selection.atom';
import { execute } from '@/graphql/execute';
import { featureSummaryQuery } from '@/queries/feature.query';
import { layerQuery } from '@/queries/layer.query';
import { ArrowUpRight } from '@mynaui/icons-react';
import { useQuery } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { useAtom } from 'jotai';
import prettyBytes from 'pretty-bytes';
import { useMemo, type FunctionComponent } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { Coords, bboxToGeoJSON } from './Coords';
import Spinner from './Spinner';
import { Button } from './ui/button';
import { Drawer, DrawerContent, DrawerFooter, DrawerHeader, DrawerTitle } from './ui/drawer';
import { Label } from './ui/label';

interface iFeaturePropertiesProps {}

const FeatureProperties: FunctionComponent<iFeaturePropertiesProps> = () => {
  const { projectId } = useParams();
  const { t } = useTranslation('components');
  const [selectedLayer, setSelectedLayer] = useAtom(layerPropertiesAtom);
  const { data, isLoading } = useQuery({
    queryKey: ['layer', selectedLayer?.id],
    queryFn: () => execute(layerQuery, { id: selectedLayer?.id! }),
    enabled: !!selectedLayer?.id,
  });

  const isVector = data?.layer?.type === 'VECTOR';

  const { data: summaryData } = useQuery({
    queryKey: ['featureSummary', selectedLayer?.id],
    queryFn: () => execute(featureSummaryQuery, { layerId: selectedLayer?.id! }),
    enabled: !!selectedLayer?.id && isVector,
  });

  const _handleClose = () => {
    setSelectedLayer(null);
    setTimeout(() => {
      document.body.style.pointerEvents = 'auto';
    }, 500);
  };

  const _handleOpenAttachment = () => {
    window.open(`/projects/${projectId}/upload/batch/${data?.layer.sourceAttachment?.batchId}`, '_blank');
  };

  const properties = useMemo(() => {
    if (!data?.layer) return [];

    const layer = data.layer;

    const bbox = layer.bboxNew?.coords as undefined | [[number, number, number], [number, number, number]];

    return [
      {
        label: t('layerProperties.id'),
        value: layer.id,
      },
      {
        label: t('layerProperties.name'),
        value: layer.name,
      },
      {
        label: t('layerProperties.sourceType'),
        value: layer.metadata?.source,
      },
      layer.sourceAttachment
        ? {
            label: t('layerProperties.sourceFile'),
            children: (
              <div className='font-medium text-sm font-mono'>
                <span
                  onClick={_handleOpenAttachment}
                  className='flex items-center gap-1 hover:underline cursor-pointer'>
                  {layer.sourceAttachment.filename} ({prettyBytes(layer.sourceAttachment?.contentLength ?? 0)})
                  <ArrowUpRight className='size-4' />
                </span>
                <span>{t('layerProperties.uploadedAt', { date: dayjs(layer.sourceAttachment.createdAt).format('DD/MM/YYYY HH:mm'), interpolation: { escapeValue: false } })}</span>
              </div>
            ),
            type: 'ATTACHMENT',
          }
        : null,
      bbox
        ? {
            label: t('layerProperties.extent'),
            children: (
              <Coords
                coords={bbox}
                geojsonProvider={() => bboxToGeoJSON(bbox, { name: layer.name })}
                geojsonFilename={`${layer.name}-extent.geojson`}
              />
            ),
            type: 'COORDS',
          }
        : null,
      layer.center
        ? {
            label: t('layerProperties.center'),
            children: <Coords coords={layer.center.coords} />,
          }
        : null,
      typeof layer.metadata.featureCount !== 'undefined'
        ? {
            label: t('layerProperties.featureCount'),
            value: layer.metadata.featureCount.toLocaleString(),
          }
        : null,
      typeof layer.metadata.imageCount !== 'undefined'
        ? {
            label: t('layerProperties.imageCount'),
            value: layer.metadata.imageCount.toLocaleString(),
          }
        : null,
      typeof layer.metadata.potreeMetadata !== 'undefined'
        ? {
            label: t('layerProperties.pointCount'),
            value: layer.metadata.potreeMetadata.points.toLocaleString(),
          }
        : null,
      layer.metadata.potreeMetadata
        ? {
            label: t('layerProperties.attributes'),
            value: (
              <ul className='list-disc list-inside pl-1'>
                {layer.metadata.potreeMetadata.attributes.map((attr: any) => (
                  <li key={attr.name} className='text-sm font-medium'>
                    {attr.name}
                  </li>
                ))}
              </ul>
            ),
          }
        : null,
      layer.metadata.potreeMetadata?.attributes?.some((a: any) => a.name === 'classification')
        ? {
            label: t('layerProperties.classifications'),
            children: `${layer.metadata.potreeMetadata.attributes.find((a: any) => a.name === 'classification').min[0]} – ${layer.metadata.potreeMetadata.attributes.find((a: any) => a.name === 'classification').max[0]}`,
          }
        : null,
    ].filter(Boolean);
  }, [data?.layer]);

  return (
    <Drawer
      onRelease={() => {
        document.body.style.pointerEvents = 'auto';
      }}
      open={!!selectedLayer}
      onClose={_handleClose}>
      <DrawerContent>
        <DrawerHeader className='w-lg'>
          <DrawerTitle>{t('layerProperties.title')}</DrawerTitle>
        </DrawerHeader>
        <div className='flex-1 overflow-y-auto max-w-160'>
          {isLoading ? (
            <div className='flex flex-col gap-4 px-4 py-2'>
              <Spinner className='size-8' />
            </div>
          ) : (
            <div className='flex flex-col gap-4 px-4 py-2'>
              {properties.map(
                (property) =>
                  property && (
                    <div key={property.label} className='flex flex-col gap-0.5'>
                      <Label className='text-xs font-medium'>{property.label}</Label>
                      {property.children ? (
                        property.children
                      ) : (
                        <span className='font-medium text-sm font-mono'>{property.value}</span>
                      )}
                    </div>
                  ),
              )}
            </div>
          )}
          {isVector && summaryData?.featureSummary && (
            <div className='flex flex-col gap-4 px-4 py-2'>
              <Label className='text-xs font-semibold uppercase tracking-wide'>{t('layerProperties.featureSummary')}</Label>
              {summaryData.featureSummary.pointCount > 0 && (
                <div className='flex flex-col gap-0.5'>
                  <Label className='text-xs font-medium'>{t('layerProperties.points')}</Label>
                  <span className='font-medium text-sm font-mono'>
                    {summaryData.featureSummary.pointCount.toLocaleString()}
                  </span>
                </div>
              )}
              {summaryData.featureSummary.lineCount > 0 && (
                <div className='flex flex-col gap-0.5'>
                  <Label className='text-xs font-medium'>{t('layerProperties.lines')}</Label>
                  <span className='font-medium text-sm font-mono'>
                    {summaryData.featureSummary.lineCount.toLocaleString()} (
                    {summaryData.featureSummary.totalLineLengthM.toLocaleString(undefined, {
                      maximumFractionDigits: 2,
                    })}{' '}
                    {t('layerProperties.metersUnit')})
                  </span>
                </div>
              )}
              {summaryData.featureSummary.polygonCount > 0 && (
                <div className='flex flex-col gap-0.5'>
                  <Label className='text-xs font-medium'>{t('layerProperties.polygons')}</Label>
                  <span className='font-medium text-sm font-mono'>
                    {summaryData.featureSummary.polygonCount.toLocaleString()} (
                    {summaryData.featureSummary.totalPolygonAreaSqm.toLocaleString(undefined, {
                      maximumFractionDigits: 2,
                    })}{' '}
                    {t('layerProperties.sqMetersUnit')},{' '}
                    {summaryData.featureSummary.totalPolygonPerimeterM.toLocaleString(undefined, {
                      maximumFractionDigits: 2,
                    })}{' '}
                    {t('layerProperties.metersCircumference')})
                  </span>
                </div>
              )}
            </div>
          )}
        </div>
        <DrawerFooter>
          <Button variant='link' onClick={_handleClose}>
            {t('layerProperties.close')}
          </Button>
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  );
};

export default FeatureProperties;
